import { Controller } from '@hotwired/stimulus';

// Brouillon enregistré automatiquement (formulaire de rapport guidé,
// form#report-form de l'élément) : `delay` ms après la dernière
// modification, et en quittant la page (pagehide, onglet masqué ; fetch
// keepalive), si le formulaire a changé depuis le dernier enregistrement ;
// jamais sur un formulaire intact ni pendant un envoi. Mêmes saisies que
// « Enregistrer le brouillon », sans fichiers ni cases « Retirer » : les
// pièces ne changent qu'avec un enregistrement explicite. État discret
// (cible status, aria-live) : enregistrement en cours, heure du dernier
// enregistrement, échec (ambre ; nouvel essai à la modification suivante).
// Brouillon enregistré : son identifiant (en-tête Report-Draft-Id) rejoint le
// formulaire (report_draft_id), pour être finalisé avec le rapport.
// Échec d'un enregistrement : copie locale des saisies (localStorage, clé
// `key` : personne, équipement, nature), effacée après un enregistrement
// ou un envoi réussi ; à l'ouverture (`restore`), une copie plus récente
// que le brouillon repris (`serverSavedAt`, ms) est proposée (cible offer :
// Restaurer ou Ignorer), jamais appliquée d'office ; au-delà de 7 jours,
// ignorée.
// Copie locale plus ancienne : ignorée (rapport fait depuis, ailleurs).
const LOCAL_COPY_MAX_AGE = 7 * 24 * 3600 * 1000;

export default class extends Controller {
    static targets = ['status', 'offer', 'offerTime'];
    static values = {
        url: String, delay: { type: Number, default: 30000 }, key: String, serverSavedAt: Number, restore: { type: Boolean, default: true },
    };

    static REMOVE_FIELD = 'report_draft[remove_attachment_ids][]';

    connect() {
        this.form = this.element.querySelector('form#report-form');
        if (!this.form) return;

        this.revision = 0;
        this.saved = 0;
        this.submitting = false;
        this.modified = (event) => this.change(event);
        this.submitStart = () => this.stop();
        this.submitEnd = (event) => {
            if (event.detail.success) this.forget();
            else this.submitting = false;
        };
        this.leave = () => this.save({ keepalive: true });
        this.visibility = () => { if (document.visibilityState === 'hidden') this.save({ keepalive: true }); };
        this.form.addEventListener('input', this.modified);
        this.form.addEventListener('change', this.modified);
        this.form.addEventListener('turbo:submit-start', this.submitStart);
        this.form.addEventListener('turbo:submit-end', this.submitEnd);
        window.addEventListener('pagehide', this.leave);
        document.addEventListener('visibilitychange', this.visibility);
        this.offer();
    }

    disconnect() {
        clearTimeout(this.timer);
        if (!this.form) return;

        this.form.removeEventListener('input', this.modified);
        this.form.removeEventListener('change', this.modified);
        this.form.removeEventListener('turbo:submit-start', this.submitStart);
        this.form.removeEventListener('turbo:submit-end', this.submitEnd);
        window.removeEventListener('pagehide', this.leave);
        document.removeEventListener('visibilitychange', this.visibility);
    }

    // Modification d'une saisie enregistrée (ni fichier ni case « Retirer »).
    change({ target }) {
        if (this.submitting || target.type === 'file' || target.name === this.constructor.REMOVE_FIELD) return;
        this.revision += 1;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.save(), this.delayValue);
    }

    // Envoi du brouillon ou du rapport : plus d'enregistrement automatique.
    stop() {
        this.submitting = true;
        clearTimeout(this.timer);
        this.request?.abort();
    }

    get pending() {
        return this.revision > this.saved;
    }

    async save({ keepalive = false } = {}) {
        clearTimeout(this.timer);
        if (!this.pending || this.submitting) return;
        if (this.request && !keepalive) {
            this.again = true;
            return;
        }

        const revision = this.revision;
        const request = new AbortController();
        this.request = request;
        this.again = false;
        this.show('saving', 'Enregistrement…');
        try {
            const response = await fetch(this.urlValue, {
                method: 'POST', body: this.payload(), keepalive, signal: request.signal, credentials: 'same-origin',
                headers: { Accept: 'text/plain', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content ?? '' },
            });
            if (response.status !== 204) throw new Error(`Brouillon refusé (${response.status})`);
            this.saved = Math.max(this.saved, revision);
            this.remember(response.headers.get('Report-Draft-Id'));
            this.forget();
            const time = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
            this.show('saved', `Brouillon enregistré à ${time}`);
        } catch (error) {
            if (error.name !== 'AbortError') {
                this.show('failed', 'Brouillon non enregistré');
                this.keep();
            }
            this.again = false;
        } finally {
            if (this.request === request) this.request = null;
        }
        if (this.again && this.pending) this.timer = setTimeout(() => this.save(), this.delayValue);
    }

    // Saisies du formulaire, sans fichiers ni retraits de pièces.
    payload() {
        const data = new FormData(this.form);
        [...new Set([...data.entries()].filter(([name, value]) => value instanceof File || name === this.constructor.REMOVE_FIELD)
            .map(([name]) => name))].forEach((name) => data.delete(name));
        data.set('autosave', '1');
        return data;
    }

    remember(id) {
        if (!id) return;
        let field = this.form.querySelector('input[name="report_draft_id"]');
        if (!field) {
            field = document.createElement('input');
            field.type = 'hidden';
            field.name = 'report_draft_id';
            this.form.prepend(field);
        }
        field.value = id;
    }

    // Copie locale (localStorage indisponible : rien).
    keep() {
        if (!this.keyValue) return;
        const fields = [...this.payload().entries()]
            .filter(([name]) => !['authenticity_token', 'autosave', 'submission_token', 'report_draft_id'].includes(name));
        try {
            window.localStorage.setItem(this.keyValue, JSON.stringify({ savedAt: Date.now(), fields }));
        } catch {
            // Stockage plein ou refusé : la saisie reste dans la page.
        }
    }

    forget() {
        if (!this.keyValue) return;
        try {
            window.localStorage.removeItem(this.keyValue);
        } catch {
            // Stockage refusé : rien à effacer.
        }
    }

    stored() {
        try {
            const copy = JSON.parse(window.localStorage.getItem(this.keyValue));
            return copy && Array.isArray(copy.fields) && Number.isFinite(copy.savedAt) ? copy : null;
        } catch {
            return null;
        }
    }

    // Copie plus récente que le brouillon repris : proposée.
    offer() {
        if (!this.keyValue || !this.restoreValue || !this.hasOfferTarget) return;
        const copy = this.stored();
        if (!copy || copy.savedAt <= this.serverSavedAtValue || Date.now() - copy.savedAt > LOCAL_COPY_MAX_AGE) return;
        if (this.hasOfferTimeTarget) {
            this.offerTimeTarget.textContent = new Date(copy.savedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
        }
        this.offerTarget.hidden = false;
    }

    // Saisies de la copie remises dans le formulaire ; les contrôleurs
    // des champs (listes, points, compteur) sont prévenus par input et change.
    restore() {
        const copy = this.stored();
        this.offerTarget.hidden = true;
        if (!copy) return;
        const values = new Map();
        copy.fields.forEach(([name, value]) => values.set(name, [...(values.get(name) || []), value]));
        const used = new Map();
        [...this.form.elements].forEach((field) => {
            if (!field.name || field.disabled || ['hidden', 'file', 'submit', 'button'].includes(field.type)) return;
            const given = values.get(field.name);
            if (field.type === 'radio' || field.type === 'checkbox') {
                field.checked = (given || []).includes(field.value);
            } else if (field.tagName === 'SELECT') {
                if (!given) return;
                [...field.options].forEach((option) => { option.selected = given.includes(option.value); });
                field.closest('[data-controller~="combobox"]')?.dispatchEvent(new CustomEvent('combobox:render'));
            } else {
                if (!given) return;
                const index = used.get(field.name) || 0;
                used.set(field.name, index + 1);
                if (index < given.length) field.value = given[index];
            }
            field.dispatchEvent(new Event('input', { bubbles: true }));
            field.dispatchEvent(new Event('change', { bubbles: true }));
        });
        this.forget();
    }

    discard() {
        this.offerTarget.hidden = true;
        this.forget();
    }

    show(state, text) {
        if (!this.hasStatusTarget) return;
        this.statusTarget.dataset.state = state;
        this.statusTarget.textContent = text;
    }
}
